import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { Alert, Button, Paper, Snackbar, Typography } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";
import type {
	ColDef,
	GetContextMenuItemsParams,
	ICellRendererParams,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CraftLetterBadge } from "./components/CraftLetterBadge";
import { UpsertDeleteCraft } from "./components/UpsertDeleteCraft";
import { useCrafts } from "./hooks/useCraftsQueries";
import type { CraftRow, ModalMode } from "./models/Craft";

const gridStyle = {
	height: "calc(100vh - 280px)",
	minHeight: 320,
	width: "100%",
};

export const CraftsPage = () => {
	const [selected, setSelected] = useState<CraftRow | null>(null);
	const [modal, setModal] = useState<ModalMode>("");
	const [notification, setNotification] = useState<string>("");
	const [error, setError] = useState<string>("");

	const { crafts, isLoading, error: listError } = useCrafts();

	useEffect(() => {
		if (listError) setError(listError.message);
	}, [listError]);

	const columns = useMemo<ColDef<CraftRow>[]>(
		() => [
			{ field: "CraftName", headerName: "Name", flex: 2 },
			{
				headerName: "Badge",
				width: 90,
				sortable: false,
				cellRenderer: (p: ICellRendererParams<CraftRow>) => (
					<CraftLetterBadge
						name={p.data?.CraftName}
						code={p.data?.Code}
						color={p.data?.Color}
					/>
				),
			},
			{ field: "WorkOrderCount", headerName: "Work Orders", width: 130 },
			{ field: "TemplateCount", headerName: "Templates", width: 120 },
		],
		[],
	);

	const openModal = useCallback((mode: ModalMode, row: CraftRow | null) => {
		setSelected(row);
		setModal(mode);
	}, []);

	const getContextMenuItems = useCallback(
		(e: GetContextMenuItemsParams<CraftRow>) => {
			const row = e?.node?.data;
			const result: MenuItemDef[] = [
				{
					name: "New Craft",
					action: () => openModal("new", null),
					icon: '<i class="fas fa-plus"></i>',
				},
			];
			if (row) {
				result.push(
					{
						name: "Edit Craft",
						action: () => openModal("edit", row),
						icon: '<i class="fas fa-edit"></i>',
					},
					{
						name: "Delete Craft",
						action: () => openModal("delete", row),
						icon: '<i class="fas fa-trash"></i>',
					},
				);
			}
			return result;
		},
		[openModal],
	);

	const closeModal = (savedMessage?: string) => {
		setModal("");
		if (savedMessage) setNotification(savedMessage);
	};

	return (
		<>
			<HelmetDexteel title="Crafts - Configuration" />
			<Grid2
				size={{ md: 12, xs: 12 }}
				sx={{ width: "100%", height: "100%", padding: 1 }}
			>
				<Typography variant="h5" style={{ fontWeight: 600 }}>
					Crafts
				</Typography>

				<Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
					The crafts a work order or template can belong to. A craft in use
					cannot be deleted.
				</Typography>

				<Paper elevation={1} sx={{ p: 1, borderRadius: "0.75rem" }}>
					<div style={gridStyle}>
						<AgGridReact<CraftRow>
							rowData={crafts}
							columnDefs={columns}
							defaultColDef={globalDefaultColDef}
							getRowId={(p) => String(p.data.CraftId)}
							rowSelection="single"
							suppressCellFocus={true}
							loading={isLoading}
							getContextMenuItems={getContextMenuItems}
							onRowDoubleClicked={(e) => openModal("edit", e.data ?? null)}
							overlayNoRowsTemplate="No crafts yet"
						/>
					</div>
				</Paper>

				<Grid2 container justifyContent="flex-end" sx={{ mt: 1 }}>
					<Grid2 size={{ md: 2, xs: 12 }}>
						<Button
							variant="contained"
							color="primary"
							onClick={() => openModal("new", null)}
							fullWidth
						>
							NEW CRAFT
						</Button>
					</Grid2>
				</Grid2>
			</Grid2>

			<UpsertDeleteCraft
				modal={modal}
				craft={modal === "new" ? null : selected}
				onHide={closeModal}
			/>

			<Snackbar
				open={!!notification}
				autoHideDuration={2500}
				onClose={() => setNotification("")}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="success" onClose={() => setNotification("")}>
					{notification}
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
